import get from 'lodash/get';

const PATIENT_ELIGIBILITY_DS = 'EBC';
const PRIOR_AUTH_AND_REFERRALS_DS = 'PAR';
const PROVIDER_DEMOGRAPHICS_DS = 'PPF';
const CLAIMS_MANAGEMENT_DS = 'CPM';
const HOSPITAL_PERF_DS = 'HPBC';
const UHC_INSIGHTS_DS = 'UI';
const UHC_REPORTS_DS = 'UR';
const UHC_WEST_CAPITATION_REPORTS_DS = 'UWCR';
const UHC_WEST_ELIGIBILITY_REPORTS_DS = 'UWER';
const UHC_WEST_PATIENT_MANAGEMENT_REPORTS_DS = 'UWPMR';
const CLAIMS_READ = 'Claim Status';
const CLAIMS_WRITE = 'Claim Adjustment';
const REFERRAL_WRITE = 'Referral Submission';

export const getTechnicalUserData = (userData) => {
  const { admin } = userData;
  const { role, provider } = admin || {};

  const userRoles = admin ? admin.userRoles : userData.userRoles;
  const isTechnicalUser = (userRoles && userRoles['admin-user']) || false;
  const isReferralLinkRIUser =
    userRoles &&
    userRoles.secure &&
    userRoles.secure.indexOf('referralLinkRI') !== -1;
  if (isTechnicalUser) {
    let technicalRedirectUrl;
    if (
      role === 'referral-intake-user' &&
      window.location.href.indexOf('/referral') === -1
    ) {
      technicalRedirectUrl = '/referral';
    }

    const { secure = [] } = userRoles;
    // If roles do not include Technical Admin then it is considered as Tier2
    // Currently used in document library
    const isNonTechnicalAdmin =
      secure.length > 0 &&
      !secure.some(
        (roles) => roles.desc === 'UHC Provider Portal Technical Admin'
      );

    return {
      ...userData,
      isTechnicalUser,
      hasTechnicalRole: !!admin,
      emulatedProvider: provider,
      isInternalUser: role === 'internal-user',
      isSuperUser: role === 'super-user' || role === 'uhc-insights-user',
      isUhcInsightsUser: role === 'uhc-insights-user',
      isIntakeUser: role === 'referral-intake-user',
      isPSAUser: role === 'provider-service-advocate-user',
      isPaanAdmin: role === 'paan-administrator-user',
      isPdoAdmin: role === 'pdo-admin-user',
      isObpUser: role === 'obp-user',
      isReferralLinkRIUser,
      technicalRedirectUrl,
      isNonTechnicalAdmin,
      isPaceAdmin: role === 'pace-admin',
    };
  }

  return {
    isTechnicalUser,
  };
};

export const getPaaPermission = (providerOrg = [], selectedProvider) => {
  if (selectedProvider && providerOrg?.length > 0) {
    const result = providerOrg.find(
      (prov) =>
        prov.corpMpin === selectedProvider.corporateProviderMpin &&
        prov.id === selectedProvider.userId
    );
    if (result && result?.paa) {
      // true or false value
      return result.paa;
    }
    return false;
  }
  return false;
};

export const getUserPermissions = (user, selectedProvider) => {
  const {
    isIntakeUser,
    isPSAUser,
    isPaanAdmin,
    allPermission,
    permissionCode,
    permissionSubCode,
    paa,
    ia,
    providerOrg,
    permissions,
  } = user;
  return {
    claims: {
      read:
        permissionCode?.includes(CLAIMS_MANAGEMENT_DS) ||
        permissions?.includes(CLAIMS_READ) ||
        allPermission,
      write: permissions?.includes(CLAIMS_WRITE) || allPermission,
    },
    eligibility: {
      read: permissionCode?.includes(PATIENT_ELIGIBILITY_DS) || allPermission,
    },
    referrals: {
      read:
        permissionCode?.includes(PRIOR_AUTH_AND_REFERRALS_DS) ||
        isIntakeUser ||
        allPermission,
      write: permissions?.includes(REFERRAL_WRITE) || allPermission,
    },
    help: {
      read: permissionCode?.includes(PATIENT_ELIGIBILITY_DS) || allPermission,
    },
    seamless: {
      read: !!user.uuid,
    },
    'prior-auth': {
      read:
        isPSAUser ||
        isPaanAdmin ||
        permissionCode?.includes(PRIOR_AUTH_AND_REFERRALS_DS) ||
        allPermission,
    },
    'my-practice-profile': {
      read: permissionCode?.includes(PROVIDER_DEMOGRAPHICS_DS) || allPermission,
    },
    'uhc-insights': {
      read: permissionCode?.includes(UHC_INSIGHTS_DS) || allPermission,
    },
    'hospital-performance': {
      read: permissionSubCode?.includes(HOSPITAL_PERF_DS) || allPermission,
    },
    'uhc-reports': {
      read: permissionSubCode?.includes(UHC_REPORTS_DS) || allPermission,
    },
    'uhc-west-reports': {
      read:
        permissionSubCode?.includes(UHC_WEST_CAPITATION_REPORTS_DS) ||
        permissionSubCode?.includes(UHC_WEST_ELIGIBILITY_REPORTS_DS) ||
        permissionSubCode?.includes(UHC_WEST_PATIENT_MANAGEMENT_REPORTS_DS) ||
        allPermission,
    },
    'submit-claim': {
      read: permissionCode?.includes(CLAIMS_MANAGEMENT_DS) || allPermission,
    },
    'single-explanation-of-benefits': {
      read: permissionCode?.includes(CLAIMS_MANAGEMENT_DS) || allPermission,
    },
    'fee-schedule-lookup': {
      read: permissionCode?.includes(PATIENT_ELIGIBILITY_DS) || allPermission,
    },
    anyPaa: paa,
    paa: selectedProvider
      ? getPaaPermission(providerOrg, selectedProvider)
      : false,
    ia,
    'document-library': {
      read: providerOrg?.length > 0,
    },
    admissionDischarge: {
      read:
        permissionCode?.includes(CLAIMS_MANAGEMENT_DS) ||
        permissionCode?.includes(PRIOR_AUTH_AND_REFERRALS_DS) ||
        allPermission,
    },
    ssbci: {
      read:
        permissionCode?.includes(CLAIMS_MANAGEMENT_DS) ||
        permissionCode?.includes(PRIOR_AUTH_AND_REFERRALS_DS) ||
        allPermission,
    },
    memberReferrals: {
      read:
        permissionCode?.includes(CLAIMS_MANAGEMENT_DS) ||
        permissionCode?.includes(PRIOR_AUTH_AND_REFERRALS_DS) ||
        allPermission,
    },
  };
};

export const validatePermissions = (user, key) => {
  const permissions = getUserPermissions(user);

  return get(permissions, key);
};

export const getUserType = (paa, ia) => {
  if (paa) {
    return 'paa';
  }
  if (ia) {
    return 'ia';
  }
  return 'su';
};

export const getUserStatus = (code) => {
  switch (code) {
    case 'DSA:V:USR:NU':
      return 'new';
    case 'DSA:V:USR:PR':
      return 'pending';
    case 'DSA:V:USR:IR':
      return 'inactive';
    default:
      return `Unknown user status code: ${code}`;
  }
};
